import { Breadcrumb, Breadcrumbs } from '@app/components/various/Breadcrumbs';
import { getHasOptimisticAdditions, getOpenOrClosedOrder, getOrderProducts } from '@app/ducks';
import { getHasAnyUnitsAdded, getIsOrderClosed } from '@app/logic/Orders';
import { compiledPaths } from '@app/paths';
import { useAllProductsLink, useAmplitude } from '@app/utils/hooks';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

interface Props {
  isAllProductsPage?: boolean;
  isSelectionPage?: boolean;
  isCheckoutPage?: boolean;
  isCmsPage?: boolean;
}
export type CheckoutBreadcrumb = 'allProducts' | 'mySelection' | 'checkout';

export const OrderTopBar = ({ isAllProductsPage = false, isSelectionPage = false, isCheckoutPage = false, isCmsPage = false }: Props) => {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation(['checkout', 'common', 'products', 'selections']);
  const orderStatus = useSelector(getOpenOrClosedOrder);
  const orderProducts = useSelector(getOrderProducts);
  const hasOptimisticAdditions = useSelector(getHasOptimisticAdditions);

  const hasAnyUnitsAdded = getHasAnyUnitsAdded(orderProducts) || hasOptimisticAdditions;
  const isOrderClosed = getIsOrderClosed(orderStatus);

  const allProductsLink = useAllProductsLink(id);
  const { trackEvent } = useAmplitude();

  const steps: Breadcrumb[] = [
    {
      dataTestId: 'productsStep',
      isActive: !isOrderClosed,
      isSelected: isAllProductsPage,
      label: t('products:all_products'),
      onClick: () => {
        trackEvent({ name: 'breadcrumbs.link.allproducts.click', properties: { context: isCmsPage ? 'cms' : 'order' } });
      },
      to: allProductsLink,
    },
    {
      dataTestId: 'selectionStep',
      isActive: !isOrderClosed,
      isSelected: isSelectionPage,
      label: t('selections:my_selection'),
      onClick: () => {
        trackEvent({ name: 'breadcrumbs.link.myselection.click', properties: { context: isCmsPage ? 'cms' : 'order' } });
      },
      to: compiledPaths.ORDER_DETAILS({ id }),
    },
    {
      dataTestId: 'checkoutStep',
      isActive: !isOrderClosed && hasAnyUnitsAdded,
      isSelected: isCheckoutPage,
      label: t('common:checkout'),
      onClick: () => {
        trackEvent({ name: 'breadcrumbs.link.checkout.click', properties: { context: isCmsPage ? 'cms' : 'order' } });
      },
      to: compiledPaths.CHECKOUT_ORDER({ id }),
    },
  ];

  return (
    <div
      className={cx(
        'sticky top-[--headerHeight] z-topBar flex w-full items-center justify-between bg-neutral-96 py-3 transition-[top] group-[.scrollingDown]/body:top-0',
        {
          'px-4 lg:px-8': isCmsPage,
          'px-4 pb-3 lg:px-8': isSelectionPage || isCheckoutPage,
          'px-4 sm:px-4 lg:px-8': isAllProductsPage,
        },
      )}
    >
      <Breadcrumbs steps={steps} title={t('checkout:checkout_steps')} />
    </div>
  );
};
